package org.example.zone360.pages.widgets.topicDiversty

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.stevdza.san.kotlinbs.components.BSTooltip
import com.stevdza.san.kotlinbs.models.TooltipDirection
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.GridEntry
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flex
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaAngleRight
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.example.zone360.components.CustomSpacer
import org.example.zone360.components.widgetTitleFontSize
import org.example.zone360.componentss.FieldTitleTextNormalSimple
import org.example.zone360.models.WidgetDetailResponse
import org.example.zone360.models.WidgetTrend
import org.example.zone360.pages.dashboard.chart.MiniBars
import org.example.zone360.pages.widgets.helper.WidgetTooltipIcon
import org.example.zone360.theme.Palette
import org.example.zone360.theme.toSitePalette
import org.example.zone360.utils.getCurrentMilliseconds
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Canvas
import org.jetbrains.compose.web.dom.CheckboxInput
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLInputElement

// ── Model ─────────────────────────────────────────────────────────────────────

data class TopicDiversityEntry(
    val label: String,
    val percentage: Int,
    val colorHex: String,
)

private val fallbackColors = listOf(
    "#4F8EF7", "#FB6F2A", "#22C55E", "#F59E0B", "#8B5CF6", "#06B6D4"
)

// ── Component ─────────────────────────────────────────────────────────────────

@Composable
fun TopicDiversityWidget(
    widgetNo: Int,
    widgetName: String,
    widgetColor: Color,
    score: Int,                          // center % in donut
    clubAverage: Int,
    entries: List<TopicDiversityEntry>,  // donut segments
    modifier: Modifier = Modifier,
    hasData: Boolean = true,
    showSelection: Boolean = false,
    sessionBorder: Boolean = false,
    trendData: List<WidgetTrend> = emptyList(),
    isSelected: Boolean = false,
    isCoachPage: Boolean = false,
    forceFullWidth: Boolean = false,
    onSelectionChange: ((Boolean) -> Unit)? = null,
    tooltipText: String = "",
    tooltipTitle: String = ""
) {
    var widgetDetailResponse by remember { mutableStateOf<WidgetDetailResponse?>(null) }
    var showModal by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val breakpoint = rememberBreakpoint()
    val sitePalette = ColorMode.current.toSitePalette()
    var isModalOpen by remember { mutableStateOf(false) }
    var isLoading by remember { mutableStateOf(false) }

    val maxEntriesToShow = if (breakpoint < Breakpoint.MD) 5 else 5
    val displayEntries = entries.take(maxEntriesToShow)
    val hasMoreEntries = entries.size > maxEntriesToShow

    // Unique IDs — same trick as GaugeChart _popup suffix
    val chartId = remember(widgetNo, showSelection) {
        val suffix = getCurrentMilliseconds()

        if (showSelection)
            "${widgetNo}_topicChart_popup_$suffix"
        else
            "${widgetNo}_topicChart_$suffix"
    }

    val colorsJs =
        if (hasData)
            entries.mapIndexed { i, e ->
                "\"${e.colorHex.ifBlank { fallbackColors[i % fallbackColors.size] }}\""
            }.joinToString(",", "[", "]")
        else
            "[\"${Palette.TopicDiversityPink.rgbLight}\"]"

    val dataJs =
        if (hasData)
            entries.joinToString(",", "[", "]") { it.percentage.toString() }
        else
            "[100]"


    val labelsJs = entries.joinToString(",", "[", "]") { "\"${it.label}\"" }
    val borderWidth = if (hasData) 3 else 0

    // Cleanup on unmount
    DisposableEffect(chartId) {
        onDispose {
            val s = document.createElement("script")
            s.textContent = """
            (function(){
                var c = Chart.getChart('$chartId');
                if(c){ c.destroy(); }
            })();
        """.trimIndent()
            document.body?.appendChild(s)
        }
    }
    // Draw / redraw chart
    LaunchedEffect(entries, score, hasData) {
        delay(100)

        val canvas = document.getElementById(chartId) as? HTMLCanvasElement
            ?: return@LaunchedEffect

        val script = """
(function(){
    var existingChart = Chart.getChart('$chartId');
    if(existingChart){ existingChart.destroy(); }

    var canvas = document.getElementById('$chartId');
    if(!canvas) return;
    canvas.width  = 220;
    canvas.height = 220;

    var centerPlugin={
        id:'center_$chartId',
        afterDraw:function(chart){
            var ctx=chart.ctx;
            ctx.save();
            ctx.font='bold 26px sans-serif';
            ctx.fillStyle='#1F2937';
            ctx.textAlign='center';
            ctx.textBaseline='middle';
            ctx.fillText('$score%', chart.width/2, chart.height/2);
            ctx.restore();
        }
    };

    new Chart('$chartId',{
        type:'doughnut',
        data:{
            labels:$labelsJs,
            datasets:[{
                backgroundColor:$colorsJs,
                data:$dataJs,
                borderWidth:$borderWidth,
                borderColor:'#ffffff'
            }]
        },
        options:{
            plugins:{legend:{display:false},title:{display:false}},
            cutout:'68%',
            responsive:false,
            maintainAspectRatio:false
        },
        plugins:[centerPlugin]
    });
})();
""".trimIndent()

        val existing = document.getElementById("topicDetailScript_$chartId")
        existing?.remove()
        val scriptEl = document.createElement("script")
        scriptEl.id = "topicDetailScript_$chartId"
        scriptEl.textContent = script
        document.body?.appendChild(scriptEl)
    }

    // ── Card shell — identical to CommonWidgetCard ────────────────────────────
    Box(
        modifier = modifier
            .then(
                when {
                    forceFullWidth -> Modifier.fillMaxHeight()   // ✅ session detail
                    sessionBorder -> Modifier.fillMaxWidth()    // ✅ session border card
                    else -> Modifier.minWidth(                  // ✅ normal dashboard
                        when {
                            breakpoint < Breakpoint.MD -> 320.px
                            showSelection -> 280.px
                            window.screen.width in 1200..1500 -> 318.px
                            else -> 350.px
                        }
                    )
                }
            )
            .backgroundColor(sitePalette.modalBackgroundColor.rgb)
            .borderRadius(if (showSelection) 12.px else 16.px)
            .padding(if (showSelection) 12.px else if (sessionBorder) 15.px else 20.px)
            .border(
                if (showSelection) 2.px else if (sessionBorder) 1.px else 0.px,
                LineStyle.Solid,
                color = Palette.CardBorder.rgbLight
            )
            .then(
                when {
                    breakpoint < Breakpoint.MD -> Modifier
                    sessionBorder -> Modifier.height(285.px)
                    !showSelection -> Modifier.height(380.px)
                    else -> Modifier
                }
            )
            .styleModifier { property("position", "relative") }
    ) {

        // ── Selection checkbox — identical to CommonWidgetCard ────────────────
        if (showSelection) {
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("position", "absolute")
                        property("top", "9px")
                        property("right", "9px")
                        property("cursor", "pointer")
                    }
                    .zIndex(9999)
            ) {
                CheckboxInput(checked = isSelected) {
                    onChange { event ->
                        onSelectionChange?.invoke(
                            (event.target as HTMLInputElement).checked
                        )
                    }
                    style {
                        property("transform", "scale(1.4)")
                        property("cursor", "pointer")
                    }
                }
            }
        }

        Column(modifier = Modifier.fillMaxWidth()
            .then(
                if (breakpoint < Breakpoint.MD) Modifier
                else Modifier.height(if (showSelection) 350.px else 340.px)
            )){

            // ── Header — identical to CommonWidgetCard ────────────────────────
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.fillMaxWidth().margin(bottom = 12.px)
            ) {
                Box(
                    modifier = Modifier
                        .width(if (sessionBorder) 24.px else 28.px)
                        .height(if (sessionBorder) 24.px else 28.px)
                        .backgroundColor(widgetColor)
                        .borderRadius(6.px)
                        .margin(right = 10.px),
                    contentAlignment = Alignment.Center
                ) {
                    FieldTitleTextNormalSimple(
                        text = widgetNo.toString(),
                        fontSize = 14.px,
                        fontWeight = 700,
                        color = sitePalette.bodyBackgroundSecondary.rgb
                    )
                }
                FieldTitleTextNormalSimple(
                    text = widgetName,
                    fontSize = widgetTitleFontSize(showSelection, sessionBorder, breakpoint),
                    fontWeight = 700
                )
                if (!showSelection) {
                    Box(modifier = Modifier.weight(1f))
                }
                if (tooltipText.isNotEmpty()) {
                    WidgetTooltipIcon(
                        tooltipTitle = tooltipTitle,
                        tooltipText = tooltipText,
                        modifier = Modifier.margin(left = 8.px)
                    )
                }
            }

            // ── Donut + legend (replaces GaugeChart block) ────────────────────
            if (hasData) {
                Row(
                    modifier = Modifier.fillMaxWidth().height(if (showSelection) 220.px else if (sessionBorder) 170.px else 200.px)
                        .margin(bottom = if (showSelection) 0.px else 8.px),
                    horizontalArrangement = if (sessionBorder) Arrangement.Center else Arrangement.SpaceAround,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    // Donut canvas
                    val size = if (sessionBorder) 95 else 120

                    Canvas(
                        Modifier
                            .width(size.px)
                            .height(size.px)
                            .id(chartId)
                            .toAttrs {
                                attr("width", size.toString())
                                attr("height", size.toString())
                            }
                    ) {}

                    // Legend list
                    Column(modifier = Modifier.margin(left = 12.px)) {
                        displayEntries.forEachIndexed { i, entry ->
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier
                                    .margin(bottom = 7.px)
                                    .styleModifier { property("width", "100%") }
                            ) {
                                // Color dot
                                Box(
                                    modifier = Modifier
                                        .width(9.px).height(9.px)
                                        .borderRadius(50.percent)
                                        .margin(right = 6.px)
                                        .styleModifier {
                                            property(
                                                "background-color",
                                                entry.colorHex.ifBlank {
                                                    fallbackColors[i % fallbackColors.size]
                                                }
                                            )
                                            property("flex-shrink", "0")
                                        }
                                )

                                // Label — flex:1 so it takes remaining space
                                Box(
                                    modifier = Modifier.flex(1)
                                ) {
                                    FieldTitleTextNormalSimple(
                                        text = entry.label,
                                        fontSize = 11.5.px,
                                        fontWeight = 500,
                                        color = Palette.CoachTalkTableHeaderText.rgbLight
                                    )
                                }

                                // Percentage — fixed width so all values line up vertically ✅
                                Box(
                                    modifier = Modifier.width(32.px),
                                    contentAlignment = Alignment.CenterEnd
                                ) {
                                    FieldTitleTextNormalSimple(
                                        text = "${entry.percentage}%",
                                        fontSize = 11.5.px,
                                        fontWeight = 700
                                    )
                                }

                            }
                        }
                    }
                }
            } else {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(190.px),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    val size = if (sessionBorder) 95 else 120

                    Canvas(
                        Modifier
                            .width(size.px)
                            .height(size.px)
                            .id(chartId)
                            .toAttrs {
                                attr("width", size.toString())
                                attr("height", size.toString())
                            }
                    ) {}
                }
            }
            // ── Club average (mirrors "Target Range" line in CommonWidgetCard) ─
            val avgLabel = when {
                sessionBorder -> "Avg"
                isCoachPage -> "Coach Avg"
                else -> "Club Avg"
            }
            CustomSpacer(
                height = when {
                    sessionBorder && !hasData -> 0.px
                    sessionBorder -> 13.px
                    else -> 0.px
                }
            )
            Box(
                modifier = Modifier.fillMaxWidth()
                    .margin(
                        bottom =
                            when {
                                showSelection && window.screen.width in 1534..1600 -> 22.px
                                showSelection -> 5.px
                                else -> 14.px
                            },
//                        top = if (showSelection || sessionBorder) 0.px else 1.px
                    ),
                contentAlignment = Alignment.Center
            ) {
                Box(
                    modifier = Modifier
                        .backgroundColor(Palette.WidgetAverageBackground.rgbLight)
                        .borderRadius(99.px)
                        .padding(leftRight = 10.px, topBottom = 4.px),
                    contentAlignment = Alignment.Center
                ) {
                    FieldTitleTextNormalSimple(
                        text = "$avgLabel: $clubAverage%",
                        fontSize = 11.px,
                        fontWeight = 500,
                        color = Palette.WidgetAverageText.rgbLight
                    )
                }
            }

            Spacer()

            // ── View Details — identical to CommonWidgetCard footer ───────────
            if (!sessionBorder) {
                if (isCoachPage) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .margin(top = if (showSelection) 24.px else 10.px),
                        contentAlignment = Alignment.Center
                    ) {
                        Box(
                            modifier = Modifier
                                .cursor(Cursor.Pointer)
                                .margin(top = 4.px)
                        ) {
                            MiniBars(
                                color = widgetColor,
                                trends = trendData
                            )
                        }
                    }
                } else {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(42.px)
//                            .margin(top = 16.px)
                            .backgroundColor(Palette.ViewDetailBackground.rgbLight)
                            .borderRadius(10.px)
                            .border(
                                1.px,
                                LineStyle.Solid,
                                color = Palette.ViewDetailBorder.rgbLight
                            )
                            .cursor(Cursor.Pointer)
                            .onClick {
                                isModalOpen = true
                            }
                    ) {
                        // Centered label
                        Box(
                            modifier = Modifier.fillMaxSize(),
                            contentAlignment = Alignment.Center
                        ) {
                            FieldTitleTextNormalSimple(
                                text = "View Details",
                                fontSize = 14.px,
                                fontWeight = 600,
                                color = Palette.ViewDetailText.rgbLight
                            )
                        }
                        // Right arrow
                        Box(
                            modifier = Modifier
                                .fillMaxHeight().fillMaxWidth()
                                .padding(right = 14.px),
                            contentAlignment = Alignment.CenterEnd
                        ) {
                            FaAngleRight(
                                modifier = Modifier
                                    .width(14.px).height(14.px)
                                    .color(Palette.ViewDetailAngle.rgbLight)
                            )
                        }
                    }
                }
            }
        }
    }

    // ── Modal placeholder (wire your own modal here later) ───────────────────
    if (isModalOpen) {
        TopicDiversityDetailsModal(
            widgetNo = widgetNo,
            widgetName = widgetName,
            widgetColor = widgetColor,
            score = score,
            hasData = hasData,
            isCoachPage = isCoachPage,
            clubAverage = clubAverage,
            widgetKey = "topic_diversity",
            entries = displayEntries,
            coaches = null,
            onClose = { isModalOpen = false }
        )
    }
}